import React, { useState, useEffect, useMemo } from 'react';
import {
  DndContext,
  DragOverEvent,
  DragStartEvent,
  DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
  closestCorners,
  useDroppable,
} from '@dnd-kit/core';
import { ProjectSidebar } from './features/projects/components/ProjectSidebar';
import { KanbanColumn } from './features/kanban/components/KanbanColumn';
import { TaskModal } from './features/tasks/components/TaskModal';
import { TrashModal } from './features/trash/components/TrashModal';
import { BackupControl } from './features/backup/components/BackupControl';
import { useKanbanStore } from './store/useKanbanStore';
import { useOfflineStore } from './store/useOfflineStore';
import { useAutoRefresh } from './features/offline/hooks/useAutoRefresh';
import { OfflineSettingsModal } from './features/offline/components/OfflineSettingsModal';
import { SyncProgressBar } from './features/offline/components/SyncProgressBar';
import { ConflictResolutionModal } from './features/offline/components/ConflictResolutionModal';
import { ConflictVotePanel } from './features/offline/components/ConflictVotePanel';
import { Task, TrashedItem } from './types';
import { Sun, Moon, Users, Menu, WifiOff } from 'lucide-react';
import { UserManagementModal } from './UserManagementModal';
import { LoginPage } from './features/auth/LoginPage';
import { SharingPanel } from './features/sharing/SharingPanel';
import { ActivityLog } from './features/activity/ActivityLog';
import { Link2, ClipboardList, LogOut } from 'lucide-react';

// Floating Drop Target Component for Drag-to-Delete
const TrashDropZone: React.FC = () => {
  const { isOver, setNodeRef } = useDroppable({
    id: 'trash-dropzone',
  });

  return (
    <div
      ref={setNodeRef}
      className={`fixed bottom-8 left-1/2 -translate-x-1/2 z-50 flex items-center gap-2 px-8 py-4 rounded-full shadow-2xl transition-all duration-200 border-2 font-bold text-sm ${
        isOver
          ? 'bg-red-600 text-white border-red-400 scale-110 shadow-red-500/50'
          : 'bg-red-500/90 text-white border-white/20 hover:bg-red-600 scale-100 animate-bounce'
      }`}
    >
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
      </svg>
      <span>{isOver ? 'Release to Delete' : 'Drop here to delete task'}</span>
    </div>
  );
};

export const App: React.FC = () => {
  const allColumns = useKanbanStore((state: any) => state.columns);

  const {
    projects,
    activeProjectId,
    tasks,
    filterHasAttachments,
    searchQuery,
    setActiveProject,
    getColumnTasks,
    moveTask,
    addColumn,
    addTask,
    updateTask,
    deleteTask,
    deleteProject,
    darkMode,
    toggleDarkMode,
    fetchInitialData,
    currentUser,
    setCurrentUser,
    userPermissions,
  } = useKanbanStore() as any;

  const {
    getBoardSettings,
    refreshPendingCounts,
    syncBoard,
    activeConflict,
    resolveConflict,
    abortConflict,
    openVotes,
    escalatedVoteId,
    startConflictVote,
    pollConflictVotes,
    submitVote,
    applyVoteResolution,
    abortVoteConflict,
    dismissEscalation,
  } = useOfflineStore();
  useAutoRefresh();

  // Auto-sync pending changes when coming back online
  useEffect(() => {
    const handleOnline = async () => {
      await refreshPendingCounts();
      const { pendingCounts } = useOfflineStore.getState();
      if (activeProjectId && (pendingCounts[activeProjectId] ?? 0) > 0) {
        await syncBoard(activeProjectId);
        void fetchInitialData();
      }
    };
    window.addEventListener('online', handleOnline);
    return () => window.removeEventListener('online', handleOnline);
  }, [refreshPendingCounts, syncBoard, activeProjectId, fetchInitialData]);

  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [isSharingPanelOpen, setIsSharingPanelOpen] = useState(false);
  const [isActivityLogOpen, setIsActivityLogOpen] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isOfflineModalOpen, setIsOfflineModalOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const token = localStorage.getItem('kan-do-token');
    if (!token) {
      setIsCheckingAuth(false);
      return undefined;
    }

    const verifySession = async () => {
      try {
        const apiUrl = import.meta.env.VITE_API_URL || '';
        const response = await fetch(`${apiUrl}/api/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok) throw new Error('Invalid session');
        const user = await response.json();
        if (!cancelled) {
          setCurrentUser(user);
          setIsAuthenticated(true);
        }
      } catch {
        localStorage.removeItem('kan-do-token');
        if (!cancelled) setIsAuthenticated(false);
      } finally {
        if (!cancelled) setIsCheckingAuth(false);
      }
    };

    void verifySession();
    return () => { cancelled = true; };
  }, [setCurrentUser]);

  useEffect(() => {
    if (isAuthenticated) void fetchInitialData();
  }, [isAuthenticated, fetchInitialData]);

  // Sync dark mode class with the root HTML element
  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [darkMode]);

  // Poll for open conflict votes on the active board every 30 seconds
  useEffect(() => {
    if (!isAuthenticated || !activeProjectId || !currentUser) return undefined;
    const poll = () => void pollConflictVotes(activeProjectId, currentUser.id as string);
    poll();
    const timer = setInterval(poll, 30_000);
    return () => clearInterval(timer);
  }, [isAuthenticated, activeProjectId, currentUser, pollConflictVotes]);

  const columns = useMemo(
    () => allColumns.filter((c: any) => c.projectId === activeProjectId),
    [allColumns, activeProjectId],
  );

  const columnTaskMap = useMemo(
    () => Object.fromEntries(columns.map((c: any) => [c.id, getColumnTasks(c.id)])),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tasks, filterHasAttachments, searchQuery, columns],
  );

  // Modal & Drag State
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
  const [isTrashModalOpen, setIsTrashModalOpen] = useState(false);
  const [isUserModalOpen, setIsUserModalOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [selectedColumnForNewTask, setSelectedColumnForNewTask] = useState<string>('col-1');
  const [activeTaskId, setActiveTaskId] = useState<string | null>(null);

  // Trash Storage State
  const [trashItems, setTrashItems] = useState<TrashedItem[]>([]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    })
  );

  const activeProject = projects.find((p: any) => p.id === activeProjectId);
  const permissions = activeProjectId ? userPermissions(activeProjectId) : { canEditTasks: false, canEditSubtasks: false, canComment: false, isOwner: false };
  const canEditTasks = permissions.canEditTasks;
  const isOwner = permissions.isOwner;


  // Handlers for Trash
  const handleMoveToTrash = (type: 'task' | 'project', id: string) => {
    if (type === 'task' && !canEditTasks) return;
    if (type === 'project' && !isOwner) return;
    if (type === 'task') {
      const taskToTrash = useKanbanStore.getState().tasks.find((t) => t.id === id);
      if (!taskToTrash) return;

      const newItem: TrashedItem = {
        id: `trash-${Date.now()}`,
        type: 'task',
        originalData: taskToTrash,
        deletedAt: new Date().toISOString(),
      };

      setTrashItems((prev) => [newItem, ...prev]);
      deleteTask(id);
    } else if (type === 'project') {
      if (!activeProject) return;

      const newItem: TrashedItem = {
        id: `trash-${Date.now()}`,
        type: 'project',
        originalData: activeProject,
        deletedAt: new Date().toISOString(),
      };

      setTrashItems((prev) => [newItem, ...prev]);
      if (deleteProject) {
        deleteProject(id);
      }
    }
  };

  const handleRestoreItem = (item: TrashedItem) => {
    if (item.type === 'task') {
      const task = item.originalData as Task;
      addTask(task);
    } else if (item.type === 'project') {
      const proj = item.originalData as any;
      useKanbanStore.setState((state: any) => ({
        projects: [...state.projects, proj],
      }));
    }
    setTrashItems((prev) => prev.filter((i) => i.id !== item.id));
  };

  const handlePermanentDelete = (trashId: string) => {
    setTrashItems((prev) => prev.filter((i) => i.id !== trashId));
  };

  const handleEmptyTrash = () => {
    setTrashItems([]);
  };

  const handleDeleteCurrentProject = () => {
    if (!activeProject) return;
    if (confirm(`Move "${activeProject.name}" and its tasks to the Trash Can?`)) {
      handleMoveToTrash('project', activeProject.id);
    }
  };

  const handleAddColumn = () => {
    if (!canEditTasks) return;
    const title = prompt('Enter column title:');
    if (!title) return;
    addColumn({
      projectId: activeProjectId,
      title,
      color: '#0284c7',
    });
  };

  const handleOpenNewTaskModal = (columnId: string) => {
    setEditingTask(null);
    setSelectedColumnForNewTask(columnId);
    setIsTaskModalOpen(true);
  };

  const handleOpenEditTaskModal = (task: Task) => {
    setEditingTask(task);
    setIsTaskModalOpen(true);
  };

  const handleSaveTask = (taskData: Partial<Task>) => {
    if (!canEditTasks) return;
    if (editingTask) {
      updateTask(editingTask.id, taskData);
    } else {
      addTask({
        title: taskData.title || 'Untitled Task',
        notes: taskData.notes,
        dueDate: taskData.dueDate,
        priority: taskData.priority,
        columnId: selectedColumnForNewTask,
        tags: taskData.tags || [],
        assignee: taskData.assignee,
        subtasks: taskData.subtasks || [],
        cost: taskData.cost,
        time: taskData.time,
      });
    }
  };

  // Drag Events
  const handleDragStart = (event: DragStartEvent) => {
    if (!canEditTasks) return;
    setActiveTaskId(String(event.active.id));
  };

  const handleDragOver = (_event: DragOverEvent) => {
    // Intentionally empty — store updates happen only on dragEnd to avoid
    // triggering dnd-kit's internal re-registration loop during drag.
  };

  const handleDragEnd = (event: DragEndEvent) => {
    if (!canEditTasks) return;
    const { active, over } = event;
    setActiveTaskId(null);

    if (!over || String(over.id) === String(active.id)) return;

    const draggingId = String(active.id);
    const overId = String(over.id);

    if (overId === 'trash-dropzone') {
      if (activeTaskId) handleMoveToTrash('task', activeTaskId);
      return;
    }

    const draggingTask = useKanbanStore.getState().tasks.find((t: any) => t.id === draggingId);
    if (!draggingTask) return;

    // Dropped onto a column droppable (empty area) — append to end of that column
    const overColumn = columns.find((c: any) => c.id === overId);
    if (overColumn) {
      if (draggingTask.columnId !== overColumn.id) {
        const targetTasks = useKanbanStore.getState().tasks.filter((t: any) => t.columnId === overColumn.id);
        moveTask(draggingId, overColumn.id, targetTasks.length);
      }
      return;
    }

    // Dropped onto another task — find its column and position
    const overTask = useKanbanStore.getState().tasks.find((t: any) => t.id === overId);
    if (!overTask) return;

    const columnTasks = useKanbanStore.getState().tasks.filter((t: any) => t.columnId === overTask.columnId);
    const overIndex = columnTasks.findIndex((t: any) => t.id === overId);
    moveTask(draggingId, overTask.columnId, overIndex);
  };

  return isCheckingAuth ? (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center text-sm text-slate-400">Checking your session…</div>
  ) : !isAuthenticated ? (
    <LoginPage onLoginSuccess={(_token, user) => { setCurrentUser(user); setIsAuthenticated(true); }} />
  ) : (
    <div className="flex h-screen bg-slate-900 overflow-hidden font-sans antialiased">
      <ProjectSidebar
        projects={projects}
        activeProjectId={activeProjectId}
        onSelectProject={setActiveProject}
        mobileOpen={isSidebarOpen}
        onMobileOpenChange={setIsSidebarOpen}
      />

      <main className="flex-1 flex flex-col min-w-0 bg-slate-200 dark:bg-slate-950 transition-colors relative">
        <header className="min-h-14 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 flex items-center gap-2 px-3 py-2 sm:px-6 sm:py-0 justify-between shrink-0 transition-colors">
          <div className="flex min-w-0 flex-1 items-center gap-2 sm:gap-3">
            <button
              type="button"
              onClick={() => setIsSidebarOpen((open) => !open)}
              className="md:hidden shrink-0 rounded-md p-1.5 text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"
              title="Open project menu"
              aria-label="Open project menu"
              aria-expanded={isSidebarOpen}
            >
              <Menu className="h-5 w-5" aria-hidden="true" />
            </button>
            <h1 className="min-w-0 truncate font-bold text-slate-800 dark:text-slate-100 text-base">
              {activeProject?.name || 'Kanban Board'}
            </h1>
            {activeProject && isOwner && (
              <button
                type="button"
                onClick={handleDeleteCurrentProject}
                className="text-xs text-slate-400 hover:text-red-600 dark:hover:text-red-400 transition-colors p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-800 flex items-center gap-1"
                title="Delete current project"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            )}
          </div>

          <div className="flex min-w-0 shrink flex-wrap items-center justify-end gap-1 sm:gap-3">
            {currentUser?.isAdmin && <button
              type="button"
              onClick={() => setIsUserModalOpen(true)}
              className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-md text-xs font-semibold transition-colors flex items-center gap-1.5 shadow-sm"
              title="Manage Users"
            >
              <Users className="w-4 h-4" />
              <span className="hidden sm:inline">Users</span>
            </button>}

            {currentUser && <span className="hidden lg:inline text-xs font-semibold text-slate-500 dark:text-slate-400">{currentUser.username}</span>}
            <button
              type="button"
              onClick={() => {
                localStorage.removeItem('kan-do-token');
                setCurrentUser(null);
                setIsAuthenticated(false);
                setIsSharingPanelOpen(false);
                setIsActivityLogOpen(false);
              }}
              className="px-2.5 py-1.5 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-md text-xs font-semibold transition-colors flex items-center gap-1.5"
              title="Log out"
            >
              <LogOut className="w-4 h-4" />
              <span className="hidden sm:inline">Logout</span>
            </button>

            {activeProject && (
              <button
                type="button"
                onClick={() => setIsOfflineModalOpen(true)}
                className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors flex items-center gap-1.5 shadow-sm ${
                  getBoardSettings(activeProjectId)?.enabled
                    ? 'bg-indigo-600 hover:bg-indigo-700 text-white'
                    : 'bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200'
                }`}
                title="Offline settings"
              >
                <WifiOff className="w-4 h-4" />
                <span className="hidden sm:inline">
                  {getBoardSettings(activeProjectId)?.enabled ? 'Offline' : 'Go Offline'}
                </span>
                {getBoardSettings(activeProjectId)?.enabled && getBoardSettings(activeProjectId)?.lastSnapshotAt && (
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 inline-block" title="Snapshot ready" />
                )}
              </button>
            )}

            {activeProject && isOwner && (
              <button
                type="button"
                onClick={() => setIsSharingPanelOpen(true)}
                className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-xs font-semibold transition-colors flex items-center gap-1.5 shadow-sm"
                title="Share this board"
              >
                <Link2 className="w-4 h-4" />
                <span className="hidden sm:inline">Share</span>
              </button>
            )}

            <button
              type="button"
              onClick={() => setIsActivityLogOpen(true)}
              className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 rounded-md text-xs font-semibold transition-colors flex items-center gap-1.5"
              title="View activity log"
            >
              <ClipboardList className="w-4 h-4" />
              <span className="hidden sm:inline">Activity</span>
            </button>

            <button
              type="button"
              onClick={toggleDarkMode}
              className="p-2 rounded-lg bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 transition-colors flex items-center justify-center"
              aria-label="Toggle Dark Mode"
              title="Toggle Dark Mode"
            >
              {darkMode ? (
                <Sun className="w-4 h-4 text-amber-400" />
              ) : (
                <Moon className="w-4 h-4 text-slate-600" />
              )}
            </button>

            <button
              type="button"
              onClick={() => setIsTrashModalOpen(true)}
              className="relative p-2 text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 rounded-md transition-colors flex items-center gap-1.5 text-xs font-semibold"
              title="Open Trash Can"
            >
              <svg className="w-5 h-5 text-slate-500 dark:text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              <span className="hidden sm:inline">Trash</span>
              {trashItems.length > 0 && (
                <span className="ml-0.5 px-1.5 py-0.2 bg-red-500 text-white rounded-full text-[10px] font-bold">
                  {trashItems.length}
                </span>
              )}
            </button>

            <BackupControl />
          </div>
        </header>

        <SyncProgressBar
          activeProjectId={activeProjectId}
          onSyncComplete={() => void fetchInitialData()}
        />

        <DndContext
          sensors={sensors}
          collisionDetection={closestCorners}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDragEnd={handleDragEnd}
        >
          <div className="flex min-h-0 flex-1 touch-scroll overflow-x-auto p-3 sm:p-6 gap-4 items-start">
            {projects.length === 0 ? (
              <div className="flex min-h-full w-full items-center justify-center py-16">
                <div className="text-center">
                  <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-blue-100 text-2xl dark:bg-blue-950/50">📋</div>
                  <h2 className="text-xl font-bold text-slate-700 dark:text-slate-100">No boards yet</h2>
                  <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">Create a board to start organizing your work.</p>
                  <button type="button" onClick={() => { const name = prompt('Enter project name:'); if (name?.trim()) useKanbanStore.getState().addProject({ name: name.trim(), color: '#3b82f6', category: 'general', tags: [] }); }} className="mt-5 rounded-lg bg-blue-600 px-4 py-2 text-sm font-bold text-white hover:bg-blue-500">Create your first board</button>
                </div>
              </div>
            ) : <>
            {columns.map((column: any) => (
              <KanbanColumn
                key={column.id}
                column={column}
                tasks={columnTaskMap[column.id] ?? []}
                onTaskClick={handleOpenEditTaskModal}
                onAddTaskClick={handleOpenNewTaskModal}
                canEditTasks={canEditTasks}
              />
            ))}

            {canEditTasks && <button
              type="button"
              onClick={handleAddColumn}
              className="w-72 sm:w-80 h-32 rounded-lg border-2 border-dashed border-slate-300 dark:border-slate-800 hover:border-slate-400 dark:hover:border-slate-700 hover:bg-slate-100/50 dark:hover:bg-slate-900/50 flex flex-col items-center justify-center text-slate-500 dark:text-slate-400 font-medium text-sm transition-colors shrink-0"
            >
              <span className="text-xl mb-1">+</span>
              <span>Add Column</span>
            </button>}
            </>}
          </div>

          {/* Floating Drop Zone visible ONLY while dragging a task card */}
          {activeTaskId && <TrashDropZone />}
        </DndContext>
      </main>

      {/* Task Creation & Edit Modal */}
      <TaskModal
        isOpen={isTaskModalOpen}
        onClose={() => setIsTaskModalOpen(false)}
        onSave={handleSaveTask}
        onDelete={(id) => handleMoveToTrash('task', id)}
        initialTask={editingTask}
        defaultColumnId={selectedColumnForNewTask}
        canEditTasks={canEditTasks}
      />

      {/* Trash Can & Recovery Modal */}
      <TrashModal
        isOpen={isTrashModalOpen}
        onClose={() => setIsTrashModalOpen(false)}
        trashItems={trashItems}
        onRestore={handleRestoreItem}
        onPermanentDelete={handlePermanentDelete}
        onEmptyTrash={handleEmptyTrash}
      />

      {/* User Management Modal */}
      <UserManagementModal
        isOpen={isUserModalOpen}
        onClose={() => setIsUserModalOpen(false)}
      />

      <SharingPanel
        isOpen={isSharingPanelOpen}
        projectId={activeProjectId}
        projectName={activeProject?.name || ''}
        onClose={() => setIsSharingPanelOpen(false)}
      />

      <ActivityLog
        isOpen={isActivityLogOpen}
        onClose={() => setIsActivityLogOpen(false)}
        projectId={activeProjectId || undefined}
      />

      {activeConflict && (
        <ConflictResolutionModal
          conflict={activeConflict}
          isSharedBoard={!isOwner}
          onResolve={(c, fields) => void resolveConflict(c, fields).then(() => void fetchInitialData())}
          onAbort={(c) => void abortConflict(c)}
          onSendToVote={(c) => void startConflictVote(c)}
        />
      )}

      {openVotes.length > 0 && currentUser && !activeConflict && (
        <ConflictVotePanel
          votes={openVotes}
          currentUserId={currentUser.id as string}
          escalatedVoteId={escalatedVoteId}
          onSubmitVote={(id, choices) => submitVote(id, choices)}
          onApplyResolution={(vote) => applyVoteResolution(vote).then(() => void fetchInitialData())}
          onAbort={(id) => abortVoteConflict(id)}
          onDismissEscalation={dismissEscalation}
        />
      )}

      {activeProject && (
        <OfflineSettingsModal
          isOpen={isOfflineModalOpen}
          onClose={() => setIsOfflineModalOpen(false)}
          boardId={activeProjectId}
          boardName={activeProject.name}
          isSharedBoard={!isOwner}
        />
      )}
    </div>
  );
};

export default App;